import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Calendar, ChevronDown, Map, Mountain } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { brand } from '@/lib/theme/tokens';

const EXPLORE_LINKS = [
  {
    to: '/activities',
    label: 'Activities',
    sub: '40 treks, ski, rides & camps',
    icon: Mountain,
    color: brand.teal,
    matchPrefixes: ['/activities'],
  },
  {
    to: '/destinations',
    label: 'Destinations',
    sub: '6 Kashmir zones',
    icon: Map,
    color: brand.chinar,
    matchPrefixes: ['/destinations'],
  },
  {
    to: '/seasons',
    label: 'When to go',
    sub: 'Month-by-month calendar',
    icon: Calendar,
    color: brand.cobalt,
    matchPrefixes: ['/seasons'],
  },
] as const;

export function ExploreNavMenu() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleEnter() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  }

  function handleLeave() {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  }

  const isActive =
    pathname === '/' ||
    EXPLORE_LINKS.some(
      (l) => l.matchPrefixes?.some((p) => pathname === p || pathname.startsWith(p + '/')),
    );

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors',
          isActive
            ? 'bg-[var(--glass-hi)] text-ink'
            : 'text-ink-dim hover:bg-[var(--glass)] hover:text-ink',
        )}
      >
        Explore
        <ChevronDown
          size={12}
          className={cn('transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute left-1/2 top-full z-50 mt-2 w-64 -translate-x-1/2 overflow-hidden rounded-2xl border"
            style={{
              background: 'var(--bg)',
              borderColor: 'var(--glass-edge-hi)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)',
            }}
          >
            <div className="p-2">
              {EXPLORE_LINKS.map(({ to, label, sub, icon: Icon, color, matchPrefixes }) => {
                const active = matchPrefixes?.some(
                  (p) => pathname === p || pathname.startsWith(p + '/'),
                );
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors',
                      active
                        ? 'bg-[var(--glass)]'
                        : 'hover:bg-[var(--glass)]',
                    )}
                  >
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg"
                      style={{ background: `${color}20`, border: `1px solid ${color}30` }}
                    >
                      <Icon size={14} style={{ color }} />
                    </span>
                    <div>
                      <div className="text-[13px] font-semibold text-ink">{label}</div>
                      <div className="text-[11px] text-ink-dim">{sub}</div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="border-t border-glass-edge px-3 py-2.5">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="block text-center text-[11.5px] font-semibold text-ink-dim transition hover:text-ink"
              >
                ← Back to homepage
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
